export const M4_UNIT_14 = {
  id: "m4-u14-vacation-travel",
  title: "Unidad 14: Vacation Time! / ¡Tiempo de Vacaciones!",
  topic: "Express yourself about travel and vacations! Learn vacation vocabulary, travel idioms, Canadian slang, and share your ideas. Talk about dream destinations, vacation plans, and travel preferences using what you know!",
  iconType: "vacation",
  grammarImage: "/grammar/grammar-m4-u14.jpg",
  grammarText: "PRESENT SIMPLE for preferences (I love beaches, I prefer mountains). PRESENT CONTINUOUS for plans (I'm planning a trip, We're visiting Toronto). CAN for possibilities (I can visit, We can't afford). WANT/WOULD LIKE for desires (I want to travel, I'd like to see). LIKE/LOVE/HATE/PREFER for opinions (I love traveling, I prefer quiet places).",
  
  sets: [
    // SET 1: Basic Vacation Vocabulary
    {
      id: "m4-u14-set1",
      title: "Set 1: Vacation Places & Activities",
      questions: [
        {
          type: "match",
          q: "Match vacation vocabulary to Spanish:",
          pairs: [
            { left: "beach", right: "playa" },
            { left: "hotel", right: "hotel" },
            { left: "sightseeing", right: "turismo/visitar lugares" }
          ],
          a: "completed",
          tip: "Basic vacation words! Beach, hotel, sightseeing = essential travel vocabulary!"
        },
        {
          type: "mc",
          q: "'I love going to the ___. I love the sun and the ocean!' (playa)",
          options: ["beach", "beach's", "beachs"],
          a: "beach",
          tip: "THE BEACH! Classic vacation spot! 🏖️ 'Go to the beach' = ir a la playa!"
        },
        {
          type: "input",
          q: "Complete: 'We are staying in a nice ___ by the water.' (hotel)",
          a: "hotel"
        },
        {
          type: "order",
          q: "Ordena: 'Me encanta hacer turismo y ver nuevos lugares'",
          words: ["I", "love", "going", "sightseeing", "and", "seeing", "new", "places."],
          a: "I love going sightseeing and seeing new places."
        },
        {
          type: "mc",
          q: "'Sightseeing' means:",
          options: [
            "Visiting tourist attractions (hacer turismo)",
            "Looking at people",
            "Having good eyesight"
          ],
          a: "Visiting tourist attractions (hacer turismo)",
          successtip: "✓ SIGHTSEEING! Tourist activity! 👀 'Go sightseeing' = hacer turismo (visit museums, monuments, attractions)!"
        },
        {
          type: "input",
          q: "Complete: 'I prefer staying in an ___ instead of a hotel.' (apartment)",
          a: "apartment"
        },
        {
          type: "mc",
          q: "'I'm ___ Paris next summer!' (planning trip)",
          options: ["visiting", "visit", "visits"],
          a: "visiting",
          tip: "Present continuous for future plans! 'I'm visiting' = voy a visitar (plan arranged)!"
        },
        {
          type: "order",
          q: "Ordena: 'Estamos planeando unas vacaciones en la montaña'",
          words: ["We", "are", "planning", "a", "mountain", "vacation."],
          a: "We are planning a mountain vacation.",
          successtip: "✓ PRESENT CONTINUOUS! Future plans! ⛰️ 'Are planning' = estamos planeando (ongoing plan process)!"
        },
        {
          type: "input",
          q: "Complete: 'I want to visit a ___ place - somewhere quiet and relaxing.' (tropical/peaceful)",
          a: "tropical / peaceful / quiet / relaxing"
        },
        {
          type: "mc",
          q: "Canadian vacation spots: 'I'm visiting ___ this summer!' (popular destinations)",
          options: [
            "Niagara Falls / Banff / Vancouver / Montreal",
            "Only Toronto",
            "The North Pole"
          ],
          a: "Niagara Falls / Banff / Vancouver / Montreal",
          tip: "Canadian destinations! Niagara Falls = cascadas famosas, Banff = montañas, Vancouver = costa, Montreal = ciudad!"
        }
      ]
    },

    // SET 2: Travel Preferences - I love/hate/prefer
    {
      id: "m4-u14-set2",
      title: "Set 2: Expressing Preferences",
      questions: [
        {
          type: "match",
          q: "Match preference verbs:",
          pairs: [
            { left: "I love", right: "Me encanta" },
            { left: "I hate", right: "Odio" },
            { left: "I prefer", right: "Prefiero" }
          ],
          a: "completed",
          tip: "State verbs! Love, hate, prefer = opinions! Use present simple!"
        },
        {
          type: "mc",
          q: "'I ___ traveling! I want to see the whole world!' (strong positive)",
          options: ["love", "am loving", "loves"],
          a: "love",
          tip: "LOVE = state verb! Present simple only! 'I love' ✓ NOT 'I am loving' ❌!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ long flights. They're so boring!' (hate)",
          a: "hate"
        },
        {
          type: "order",
          q: "Ordena: 'Prefiero la playa a las montañas'",
          words: ["I", "prefer", "the", "beach", "to", "the", "mountains."],
          a: "I prefer the beach to the mountains."
        },
        {
          type: "mc",
          q: "'I ___ quiet vacations. I don't like busy tourist places.' (preference)",
          options: ["prefer", "am preferring", "prefers"],
          a: "prefer",
          successtip: "✓ PREFER! State verb! 🏝️ vs 🏙️ 'Prefer' = present simple to show preference!"
        },
        {
          type: "input",
          q: "Complete: 'My wife loves museums, but I ___ outdoor activities.' (prefer)",
          a: "prefer"
        },
        {
          type: "mc",
          q: "'I ___ staying in hostels. They're cheap and you meet people!' (like)",
          options: ["like / love", "am liking", "likes"],
          a: "like / love",
          tip: "LIKE/LOVE = state verbs! Present simple! 'I like' = me gusta. 'I love' = me encanta!"
        },
        {
          type: "order",
          q: "Ordena: 'No me gusta volar - prefiero conducir'",
          words: ["I", "don't", "like", "flying", "-", "I", "prefer", "driving."],
          a: "I don't like flying - I prefer driving.",
          successtip: "✓ CONTRASTING PREFERENCES! Natural! ✈️ vs 🚗 Don't like + prefer = showing two opinions!"
        },
        {
          type: "input",
          q: "Complete: 'Some people love adventure, but I ___ relaxing vacations.' (prefer/like)",
          a: "prefer / like"
        },
        {
          type: "mc",
          q: "Personal expression: 'What do you prefer - beach or mountains?' You answer:",
          options: [
            "YOUR opinion! 'I prefer beaches' OR 'I prefer mountains'",
            "Yes",
            "No"
          ],
          a: "YOUR opinion! 'I prefer beaches' OR 'I prefer mountains'",
          tip: "EXPRESS YOURSELF! No right answer! Share YOUR preference! This is about YOU!"
        }
      ]
    },

    // SET 3: Travel Idioms & Expressions
    {
      id: "m4-u14-set3",
      title: "Set 3: Travel Idioms (Common Expressions)",
      questions: [
        {
          type: "match",
          q: "Match idiom to meaning:",
          pairs: [
            { left: "hit the road", right: "start traveling / salir de viaje" },
            { left: "catch some rays", right: "sunbathe / tomar sol" },
            { left: "get away", right: "take a vacation / escaparse" }
          ],
          a: "completed",
          tip: "IDIOMS! Colorful expressions! Native speakers use these ALL the time!"
        },
        {
          type: "mc",
          q: "'Let's ___ the road! The trip is starting!' (idiom = empezar el viaje)",
          options: ["hit", "beat", "kick"],
          a: "hit",
          tip: "'HIT THE ROAD'! Start traveling! 🚗 Very common idiom! 'Let's hit the road' = ¡Vamos!"
        },
        {
          type: "input",
          q: "Complete: 'I'm so stressed. I need to ___ away for a few days.' (get = escape)",
          a: "get"
        },
        {
          type: "order",
          q: "Ordena: 'Vamos a la playa a tomar sol'",
          words: ["Let's", "go", "to", "the", "beach", "and", "catch", "some", "rays!"],
          a: "Let's go to the beach and catch some rays!"
        },
        {
          type: "mc",
          q: "'Catch some rays' means:",
          options: [
            "Sunbathe / get sunshine (tomar sol)",
            "Catch fish",
            "Take photos"
          ],
          a: "Sunbathe / get sunshine (tomar sol)",
          successtip: "✓ CATCH SOME RAYS! Beach idiom! ☀️ Rays = sun rays (rayos del sol). Fun expression!"
        },
        {
          type: "input",
          q: "Complete: 'I'm saving money to ___ away next year.' (get)",
          a: "get"
        },
        {
          type: "mc",
          q: "'I want to ___ off the beaten path' means:",
          options: [
            "Go somewhere not touristy / explore unusual places",
            "Stay on the main road",
            "Get lost"
          ],
          a: "Go somewhere not touristy / explore unusual places",
          tip: "'OFF THE BEATEN PATH'! Idiom! 🗺️ = lugares no turísticos (explore hidden/authentic places)!"
        },
        {
          type: "order",
          q: "Ordena: 'Preferimos lugares fuera de lo común, auténticos'",
          words: ["We", "prefer", "places", "off", "the", "beaten", "path."],
          a: "We prefer places off the beaten path.",
          successtip: "✓ OFF THE BEATEN PATH! Traveler preference! 🌍 = avoid tourist traps, find authentic experiences!"
        },
        {
          type: "input",
          q: "Complete: 'This trip is breaking the ___!' (bank = very expensive)",
          a: "bank"
        },
        {
          type: "mc",
          q: "'Breaking the bank' means:",
          options: [
            "Very expensive / costs too much money",
            "Going to a bank",
            "Saving money"
          ],
          a: "Very expensive / costs too much money",
          tip: "'BREAK THE BANK'! Money idiom! 💰 = demasiado caro! 'This hotel is breaking the bank!'"
        }
      ]
    },

    // SET 4: Canadian Travel Slang
    {
      id: "m4-u14-set4",
      title: "Set 4: Canadian Travel Slang (Informal Language)",
      questions: [
        {
          type: "match",
          q: "Match Canadian slang to meaning:",
          pairs: [
            { left: "road trip", right: "viaje en carro (long drive)" },
            { left: "staycation", right: "vacation at home" },
            { left: "cottage", right: "casa de verano (by lake/woods)" }
          ],
          a: "completed",
          tip: "SLANG! Informal everyday language! This is how Canadians REALLY talk!"
        },
        {
          type: "mc",
          q: "'We're doing a ___ to Vancouver - 8 hours driving!' (viaje en carro)",
          options: ["road trip", "trip road", "road vacation"],
          a: "road trip",
          tip: "ROAD TRIP! Super common! 🚗 = viaje largo en carro. Canadian summers = road trips!"
        },
        {
          type: "input",
          q: "Complete: 'We can't afford a trip, so we're having a ___ this year.' (staycation)",
          a: "staycation"
        },
        {
          type: "order",
          q: "Ordena: 'Vamos al cottage este fin de semana'",
          words: ["We're", "going", "to", "the", "cottage", "this", "weekend."],
          a: "We're going to the cottage this weekend."
        },
        {
          type: "mc",
          q: "'Cottage' in Canadian English means:",
          options: [
            "Summer house by lake or in woods (casa de veraneo)",
            "Small house in city",
            "Hotel"
          ],
          a: "Summer house by lake or in woods (casa de veraneo)",
          successtip: "✓ COTTAGE! Canadian culture! 🏡 Family cottage = vacation home. Many Canadians go 'to the cottage' in summer!"
        },
        {
          type: "input",
          q: "Complete: 'A ___ is a vacation at home - no traveling!' (staycation)",
          a: "staycation"
        },
        {
          type: "mc",
          q: "'Staycation' = stay + vacation means:",
          options: [
            "Vacation at home or nearby (no traveling far)",
            "Staying in a hotel",
            "Working during vacation"
          ],
          a: "Vacation at home or nearby (no traveling far)",
          tip: "STAYCATION! Modern word! 🏠 = vacaciones en casa (cheaper, less stressful, still fun!)!"
        },
        {
          type: "order",
          q: "Ordena: 'Estamos tomando un finde largo en Montreal'",
          words: ["We're", "taking", "a", "long", "weekend", "in", "Montreal."],
          a: "We're taking a long weekend in Montreal.",
          successtip: "✓ LONG WEEKEND! Common Canadian break! 📅 3-4 day weekend = perfect for short trips!"
        },
        {
          type: "input",
          q: "Complete: 'I'm so ___ out. I need a vacation!' (burned = exhausted from work)",
          a: "burned / burnt"
        },
        {
          type: "mc",
          q: "'Burned out' or 'burnt out' (slang) means:",
          options: [
            "Exhausted from too much work (agotado/quemado)",
            "On fire",
            "Sunburned"
          ],
          a: "Exhausted from too much work (agotado/quemado)",
          tip: "'BURNED OUT'! Work stress slang! 😫 = agotado mentalmente. 'I'm burned out, I need a break!'"
        }
      ]
    },

    // SET 5: Planning & Talking About Vacations
    {
      id: "m4-u14-set5",
      title: "Set 5: Making Vacation Plans",
      questions: [
        {
          type: "match",
          q: "Match planning phrases:",
          pairs: [
            { left: "I'm thinking of", right: "Estoy pensando en" },
            { left: "I'm planning to", right: "Estoy planeando" },
            { left: "I'm hoping to", right: "Espero poder" }
          ],
          a: "completed",
          tip: "Planning expressions! 'Thinking of/planning to/hoping to' + verb!"
        },
        {
          type: "mc",
          q: "'I'm ___ going to Cuba next year.' (thinking about possibility)",
          options: ["thinking of", "think of", "thinks of"],
          a: "thinking of",
          tip: "'THINKING OF'! Considering idea! 💭 'Thinking of + -ing' = considering (estoy pensando en)!"
        },
        {
          type: "input",
          q: "Complete: 'We're ___ to visit Europe this summer.' (planning)",
          a: "planning"
        },
        {
          type: "order",
          q: "Ordena: 'Espero poder ir a Japón algún día'",
          words: ["I'm", "hoping", "to", "go", "to", "Japan", "someday."],
          a: "I'm hoping to go to Japan someday."
        },
        {
          type: "mc",
          q: "'I'm hoping to travel more' means:",
          options: [
            "I want/wish to travel more (desire, not certain)",
            "I'm definitely traveling",
            "I hate traveling"
          ],
          a: "I want/wish to travel more (desire, not certain)",
          successtip: "✓ HOPING TO! Wish/desire! 🤞 = espero poder (want to but not sure if possible)!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ to see Italy before I'm 40.' (want)",
          a: "want"
        },
        {
          type: "mc",
          q: "'I'd like to visit' is more ___ than 'I want to visit'.",
          options: ["polite / formal", "rude", "the same"],
          a: "polite / formal",
          tip: "'I'D LIKE TO' = more polite! 'I want' = direct. 'I'd like' = softer, more polite!"
        },
        {
          type: "order",
          q: "Ordena: 'Me gustaría ver las Cataratas del Niágara'",
          words: ["I'd", "like", "to", "see", "Niagara", "Falls."],
          a: "I'd like to see Niagara Falls."
        },
        {
          type: "input",
          q: "Complete: 'Where are you ___ this summer?' (going - plans)",
          a: "going"
        },
        {
          type: "mc",
          q: "Common vacation question: 'Do you have any plans for ___?'",
          options: [
            "summer / the holidays / your vacation",
            "yesterday",
            "last year"
          ],
          a: "summer / the holidays / your vacation",
          successtip: "✓ COMMON QUESTION! Small talk! 🗣️ 'Any plans for summer?' = typical Canadian conversation starter!"
        }
      ]
    },

    // SET 6: Can/Can't for Travel Possibilities
    {
      id: "m4-u14-set6",
      title: "Set 6: What You Can/Can't Do",
      questions: [
        {
          type: "match",
          q: "Match ability/possibility:",
          pairs: [
            { left: "I can afford", right: "Puedo pagar/costear" },
            { left: "I can't afford", right: "No puedo costear" },
            { left: "We can visit", right: "Podemos visitar" }
          ],
          a: "completed",
          tip: "CAN/CAN'T = ability/possibility! Common for discussing money and time!"
        },
        {
          type: "mc",
          q: "'I ___ afford an expensive hotel. Let's find something cheaper.' (negative)",
          options: ["can't", "can", "not"],
          a: "can't",
          tip: "'CAN'T AFFORD'! Money limitation! 💰 = no puedo costear. Very common travel phrase!"
        },
        {
          type: "input",
          q: "Complete: 'We ___ visit three cities in one week if we plan well.' (can)",
          a: "can"
        },
        {
          type: "order",
          q: "Ordena: 'Podemos tomar el tren - es más barato que volar'",
          words: ["We", "can", "take", "the", "train", "-", "it's", "cheaper", "than", "flying."],
          a: "We can take the train - it's cheaper than flying."
        },
        {
          type: "mc",
          q: "'I want to go to Australia, but I ___ afford it right now.' (can/can't)",
          options: ["can't", "can", "am"],
          a: "can't",
          successtip: "✓ REALISTIC PLANNING! Honest! 🇦🇺 'Want BUT can't afford' = desire vs reality. Honest conversation!"
        },
        {
          type: "input",
          q: "Complete: 'You ___ see the Northern Lights in Canada!' (can - possibility)",
          a: "can"
        },
        {
          type: "mc",
          q: "'I ___ only take one week off work.' (limitation)",
          options: ["can", "can't", "am"],
          a: "can",
          tip: "'CAN ONLY' = limitation! 'Can only' = solo puedo. Shows limit on what's possible!"
        },
        {
          type: "order",
          q: "Ordena: 'No podemos viajar en diciembre - estamos trabajando'",
          words: ["We", "can't", "travel", "in", "December", "-", "we're", "working."],
          a: "We can't travel in December - we're working.",
          successtip: "✓ CAN'T + REASON! Explaining! 📅 Can't travel BECAUSE working. Giving reason for limitation!"
        },
        {
          type: "input",
          q: "Complete: 'If we save money, we ___ go next year.' (can)",
          a: "can"
        },
        {
          type: "mc",
          q: "'We can stay with friends' - This is good because:",
          options: [
            "It's free / saves money on hotels",
            "It's more expensive",
            "We can't do it"
          ],
          a: "It's free / saves money on hotels",
          tip: "STAY WITH FRIENDS! Budget travel! 👥 = free accommodation! Common way to save money!"
        }
      ]
    },

    // SET 7: Vacation Types & Styles
    {
      id: "m4-u14-set7",
      title: "Set 7: Types of Vacations",
      questions: [
        {
          type: "match",
          q: "Match vacation type to description:",
          pairs: [
            { left: "adventure vacation", right: "hiking, sports, activities" },
            { left: "relaxing vacation", right: "beach, spa, no stress" },
            { left: "cultural vacation", right: "museums, history, local food" }
          ],
          a: "completed",
          tip: "Different vacation styles! Which type do YOU prefer? Express yourself!"
        },
        {
          type: "mc",
          q: "'I love ___ vacations - hiking, kayaking, rock climbing!' (active)",
          options: ["adventure", "relaxing", "boring"],
          a: "adventure",
          tip: "ADVENTURE! Active vacation! 🏔️ For people who love sports and activities!"
        },
        {
          type: "input",
          q: "Complete: 'I prefer ___ vacations - beach, books, doing nothing!' (relaxing/quiet)",
          a: "relaxing / quiet / peaceful"
        },
        {
          type: "order",
          q: "Ordena: 'Me encanta aprender sobre historia y cultura local'",
          words: ["I", "love", "learning", "about", "history", "and", "local", "culture."],
          a: "I love learning about history and local culture."
        },
        {
          type: "mc",
          q: "'Cultural vacation' focuses on:",
          options: [
            "Learning about local culture, museums, food, history",
            "Only sports",
            "Only beaches"
          ],
          a: "Learning about local culture, museums, food, history",
          successtip: "✓ CULTURAL TOURISM! Educational! 🏛️ Museums, local food, traditions, history = cultural experience!"
        },
        {
          type: "input",
          q: "Complete: 'I'm planning a ___ trip - just me, no group!' (solo)",
          a: "solo"
        },
        {
          type: "mc",
          q: "'Solo travel' means:",
          options: [
            "Traveling alone (viajar solo/a)",
            "Traveling with family",
            "Group tour"
          ],
          a: "Traveling alone (viajar solo/a)",
          tip: "SOLO TRAVEL! Independent! 🎒 Traveling alone = freedom, meet people, challenge yourself!"
        },
        {
          type: "order",
          q: "Ordena: 'Algunos prefieren tours de grupo, yo prefiero viajar solo'",
          words: ["Some", "people", "prefer", "group", "tours,", "but", "I", "prefer", "solo", "travel."],
          a: "Some people prefer group tours, but I prefer solo travel.",
          successtip: "✓ CONTRASTING STYLES! Personal preference! 👥 vs 🚶 Different people like different styles!"
        },
        {
          type: "input",
          q: "Complete: 'A ___ vacation is romantic - just two people.' (couples/honeymoon)",
          a: "couples / honeymoon / romantic"
        },
        {
          type: "mc",
          q: "YOUR OPINION: What type of vacation do YOU prefer?",
          options: [
            "Share YOUR preference! Adventure/Relaxing/Cultural/Solo/Group?",
            "Yes",
            "No"
          ],
          a: "Share YOUR preference! Adventure/Relaxing/Cultural/Solo/Group?",
          tip: "EXPRESS YOURSELF! No wrong answer! What's YOUR vacation style? Explain why!"
        }
      ]
    },

    // SET 8: Travel Problems & Solutions
    {
      id: "m4-u14-set8",
      title: "Set 8: Travel Challenges",
      questions: [
        {
          type: "match",
          q: "Match problem to solution:",
          pairs: [
            { left: "expensive", right: "travel off-season / stay in hostels" },
            { left: "no time", right: "take a long weekend / short trip" },
            { left: "can't decide", right: "research / ask for recommendations" }
          ],
          a: "completed",
          tip: "Problem-solving! Every challenge has solutions! Think creatively!"
        },
        {
          type: "mc",
          q: "'Hotels are too expensive. We're staying in a ___.' (cheap option)",
          options: ["hostel / Airbnb", "palace", "mansion"],
          a: "hostel / Airbnb",
          tip: "BUDGET OPTIONS! 💰 Hostel = dormitory style (cheap). Airbnb = rent apartment/room!"
        },
        {
          type: "input",
          q: "Complete: 'Flights are cheaper in ___ season.' (off = not peak tourist time)",
          a: "off"
        },
        {
          type: "order",
          q: "Ordena: 'Viajo fuera de temporada alta para ahorrar dinero'",
          words: ["I", "travel", "off-season", "to", "save", "money."],
          a: "I travel off-season to save money."
        },
        {
          type: "mc",
          q: "'Off-season' means:",
          options: [
            "Not the busy tourist time (temporada baja - cheaper, less crowded)",
            "The best time to visit",
            "Winter only"
          ],
          a: "Not the busy tourist time (temporada baja - cheaper, less crowded)",
          successtip: "✓ OFF-SEASON! Smart travel! 📅 Low season = cheaper prices, fewer tourists! High season = expensive, crowded!"
        },
        {
          type: "input",
          q: "Complete: 'I don't have much vacation time, so I'm taking a ___ weekend.' (long)",
          a: "long"
        },
        {
          type: "mc",
          q: "'I'm jet-lagged' means:",
          options: [
            "Tired from time zone changes (cansado por cambio de horario)",
            "I missed my flight",
            "I'm excited"
          ],
          a: "Tired from time zone changes (cansado por cambio de horario)",
          tip: "JET LAG! Travel tiredness! 😴 = desfase horario. After long flights across time zones!"
        },
        {
          type: "order",
          q: "Ordena: 'No puedo decidir a dónde ir - hay tantas opciones'",
          words: ["I", "can't", "decide", "where", "to", "go", "-", "there", "are", "so", "many", "options!"],
          a: "I can't decide where to go - there are so many options!"
        },
        {
          type: "input",
          q: "Complete: 'Can you ___ a good place to visit in Canada?' (recommend)",
          a: "recommend"
        },
        {
          type: "mc",
          q: "Travel advice: 'I want to travel but I'm nervous about traveling alone.' Response:",
          options: [
            "'Start with a short solo trip nearby to build confidence!'",
            "'Never travel alone'",
            "'Only go with groups always'"
          ],
          a: "'Start with a short solo trip nearby to build confidence!'",
          successtip: "✓ BUILDING CONFIDENCE! Good advice! 💪 Start small → build confidence → bigger adventures!"
        }
      ]
    },

    // SET 9: More Travel Idioms & Slang
    {
      id: "m4-u14-set9",
      title: "Set 9: More Travel Expressions",
      questions: [
        {
          type: "mc",
          q: "'I have itchy feet' (idiom) means:",
          options: [
            "I want to travel / feeling restless (ganas de viajar)",
            "My feet hurt",
            "I have a foot problem"
          ],
          a: "I want to travel / feeling restless (ganas de viajar)",
          tip: "'ITCHY FEET'! Travel desire idiom! 🦶✈️ = ganas de viajar. 'I have itchy feet - I need to go somewhere!'"
        },
        {
          type: "input",
          q: "Complete: 'I have the travel ___!' (bug = strong desire to travel)",
          a: "bug"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo ganas de viajar - necesito una aventura'",
          words: ["I", "have", "itchy", "feet", "-", "I", "need", "an", "adventure!"],
          a: "I have itchy feet - I need an adventure!"
        },
        {
          type: "mc",
          q: "'The travel bug' means:",
          options: [
            "Strong desire/passion for traveling (pasión por viajar)",
            "An insect",
            "Being sick while traveling"
          ],
          a: "Strong desire/passion for traveling (pasión por viajar)",
          successtip: "✓ TRAVEL BUG! Passion idiom! 🦟✈️ = pasión por viajar. 'I caught the travel bug!' = me encanta viajar ahora!"
        },
        {
          type: "input",
          q: "Complete: 'This place is a tourist ___.' (trap = overpriced, not authentic)",
          a: "trap"
        },
        {
          type: "mc",
          q: "'Tourist trap' means:",
          options: [
            "Place that's overpriced and not authentic (trampa turística)",
            "Good deal",
            "Hidden gem"
          ],
          a: "Place that's overpriced and not authentic (trampa turística)",
          tip: "TOURIST TRAP! Warning! 🪤 = lugar trampa (overpriced, low quality, fake). Avoid tourist traps!"
        },
        {
          type: "order",
          q: "Ordena: 'Este restaurante es una trampa turística - muy caro'",
          words: ["This", "restaurant", "is", "a", "tourist", "trap", "-", "it's", "way", "too", "expensive!"],
          a: "This restaurant is a tourist trap - it's way too expensive!"
        },
        {
          type: "input",
          q: "Complete: 'This café is a hidden ___!' (gem = great place few people know)",
          a: "gem"
        },
        {
          type: "mc",
          q: "'Hidden gem' means:",
          options: [
            "Great place not many tourists know about (joya escondida)",
            "Expensive jewelry",
            "Tourist trap"
          ],
          a: "Great place not many tourists know about (joya escondida)",
          successtip: "✓ HIDDEN GEM! Discovery! 💎 = joya escondida (authentic, special place). 'This beach is a hidden gem!'"
        },
        {
          type: "mc",
          q: "'I'm living out of a suitcase' means:",
          options: [
            "Traveling constantly, no permanent home right now",
            "I lost my house",
            "I have a small suitcase"
          ],
          a: "Traveling constantly, no permanent home right now",
          tip: "'LIVING OUT OF A SUITCASE'! Travel lifestyle! 🧳 = viviendo de hotel en hotel (constant travel)!"
        }
      ]
    },

    // SET 10: Personal Travel Stories & Opinions
    {
      id: "m4-u14-set10",
      title: "Set 10: Sharing Travel Experiences",
      questions: [
        {
          type: "mc",
          q: "Starting a travel story: '___' (opener)",
          options: [
            "'Last summer, I visited...' OR 'I'm planning to...'",
            "'Yes'",
            "'No'"
          ],
          a: "'Last summer, I visited...' OR 'I'm planning to...'",
          tip: "STORY STARTERS! 📖 Past = 'Last summer I visited'. Future = 'I'm planning to'. Share YOUR story!"
        },
        {
          type: "input",
          q: "Complete: 'My favorite place I visited is ___.' (YOUR answer!)",
          a: "(Your personal answer)"
        },
        {
          type: "order",
          q: "Ordena: 'El mejor viaje de mi vida fue a...'",
          words: ["The", "best", "trip", "of", "my", "life", "was", "to..."],
          a: "The best trip of my life was to..."
        },
        {
          type: "mc",
          q: "Asking for recommendations: 'Where do you recommend ___?'",
          options: [
            "visiting in Canada / going for vacation / staying",
            "yesterday",
            "always"
          ],
          a: "visiting in Canada / going for vacation / staying",
          successtip: "✓ ASKING FOR ADVICE! Natural conversation! 🗣️ 'Where do you recommend?' = common travel question!"
        },
        {
          type: "input",
          q: "Complete: 'If you love nature, I ___ visiting Banff!' (recommend)",
          a: "recommend"
        },
        {
          type: "mc",
          q: "Expressing strong opinion: 'You ___ see Niagara Falls! It's amazing!' (strong suggestion)",
          options: ["have to / must / should definitely", "maybe", "don't"],
          a: "have to / must / should definitely",
          tip: "'HAVE TO' / 'MUST' / 'SHOULD DEFINITELY' = strong recommendation! Shows enthusiasm!"
        },
        {
          type: "order",
          q: "Ordena: 'Tienes que visitar Montreal - es hermoso'",
          words: ["You", "have", "to", "visit", "Montreal", "-", "it's", "beautiful!"],
          a: "You have to visit Montreal - it's beautiful!"
        },
        {
          type: "input",
          q: "Complete: 'On my dream vacation, I want to ___.' (YOUR dream!)",
          a: "(Your personal dream - any answer!)"
        },
        {
          type: "mc",
          q: "Sharing preferences: 'Some people love adventure, but I'm ___.'",
          options: [
            "more of a relaxing vacation person / into cultural tourism",
            "yes",
            "always"
          ],
          a: "more of a relaxing vacation person / into cultural tourism",
          successtip: "✓ PERSONAL EXPRESSION! Authentic! 💭 'I'm more of a...' = showing YOUR personality! Be yourself!"
        },
        {
          type: "mc",
          q: "COMPLETE CONVERSATION:\n\nFriend: 'Do you have any vacation plans?'\nYou: 'Yes! I'm ___ visiting Vancouver this summer.'\nFriend: 'Nice! What do you want to do there?'\nYou: 'I ___ hiking and seeing the mountains. What about you?'\nFriend: 'I prefer the beach. I'm ___ a beach vacation.'\nYou: 'That sounds great! Where can you go that's not too ___?'\nFriend: 'I'm ___ Mexico - it's beautiful and not expensive.'\nYou: 'Perfect! I ___ you're going to love it!'\n\nCorrect words:",
        options: [
          "planning / love / thinking of / expensive / thinking of / think",
          "plan / loving / think / cheap / thought / thought",
          "plans / likes / thinks / money / think / thinking"
        ],
        a: "planning / love / thinking of / expensive / thinking of / think",
        successtip: "✓ UNIT 14 MASTERED! Natural travel conversation! 🎉 Using: present continuous for plans (I'm planning), state verbs for preferences (I love), thinking of (considering), asking questions (Where can you go?), giving opinions (I think). This IS how friends talk about vacation! You're expressing yourself naturally! Perfect receptivity to all learned structures! From vocabulary to idioms to real conversation! You can now discuss travel dreams, preferences, plans, and give/receive recommendations! 🌍✈️🗺️"
     }
      ]
    }
  ],
  // ===================================================================
// READING SET: Travel Discussion (Vacation Preferences)
// ===================================================================
readingSet: {
  id: "m4-u14-reading",
  title: "Reading: Travel Discussion Forum",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Initial Request & Mountains (preguntas 1, 2, 4, 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Online Forum: Travel Dreams**

**TravelLover99:** Hey everyone! I have **itchy feet**! I'm so **burned out** from work and I need to get away. Where do you recommend visiting in Canada? I can only take one week off.

**MountainGirl:** I LOVE Banff! If you prefer mountains and nature, you have to go there! It's not a **tourist trap** - it's authentic and beautiful. I'm planning to go back next summer!`
    },
    {
      type: "mc",
      q: "¿Qué significa la expresión 'I have itchy feet' en este contexto?",
      options: [
        "TravelLover99 tiene muchas ganas de viajar",
        "Tiene un problema médico en los pies",
        "Tiene los pies cansados de tanto caminar"
      ],
      a: "TravelLover99 tiene muchas ganas de viajar",
      successtip: "✓ IDIOM!\n'Itchy feet' (pies con picazón) es una forma muy común de decir que no puedes quedarte quieto y necesitas viajar. ✈️"
    },
    {
      type: "mc",
      q: "¿Qué significa que alguien se siente 'burned out'?",
      options: [
        "Agotado físicamente y mentalmente por el trabajo",
        "Quemado por el sol",
        "Que tiene mucho calor"
      ],
      a: "Agotado físicamente y mentalmente por el trabajo",
      successtip: "✓ PHRASAL VERB!\n'Burned out' describe el agotamiento extremo. ¡Por eso necesita vacaciones! 😫"
    },
    {
      type: "mc",
      q: "¿Cuál es la diferencia entre un 'tourist trap' y un 'hidden gem'?",
      options: [
        "Tourist trap = malo (caro y artificial); Hidden gem = bueno (auténtico)",
        "Ambos significan lo mismo",
        "Ambos son lugares peligrosos para viajar"
      ],
      a: "Tourist trap = malo (caro y artificial); Hidden gem = bueno (auténtico)",
      successtip: "✓ TRAVEL VOCABULARY!\nBanff es auténtico, no una 'trampa para turistas' diseñada solo para sacar dinero. 💎"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Beach vs. Budget (preguntas 6 y 7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Different Styles**

**BeachBum23:** I prefer beaches, so I'm thinking of going to Vancouver Island. You can catch some rays and go whale watching. It's perfect for a relaxing vacation.

**BudgetTraveler:** Both sound expensive! I can't afford fancy hotels. Any cheaper options?

**MountainGirl:** Stay in a hostel! Or travel **off-season**. I always travel in September or October - it's cheaper and there are fewer tourists.`
    },
    {
      type: "mc",
      q: "¿Qué consejo le dan a BudgetTraveler para ahorrar dinero?",
      options: [
        "Quedarse en un hostal o viajar en temporada baja (off-season)",
        "Quedarse en hoteles de lujo",
        "No viajar este año"
      ],
      a: "Quedarse en un hostal o viajar en temporada baja (off-season)",
      successtip: "✓ BUDGET TIPS!\nViajar fuera de la temporada alta es la mejor forma de ahorrar en Canadá. 💰"
    },
    {
      type: "mc",
      q: "En el texto se usa mucho el verbo 'prefer'. ¿A qué se debe?",
      options: [
        "A que todos están expresando sus gustos personales (state verb)",
        "A que no saben usar otros verbos",
        "Es un error de repetición"
      ],
      a: "A que todos están expresando sus gustos personales (state verb)",
      successtip: "✓ PREFERENCES!\n'Prefer' es un verbo de estado ideal para debates sobre gustos. 💭"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Solo, Family, and Culture (preguntas 3 y 8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Variety of Plans**

**SoloAdventurer:** I love solo travel! I'm planning a road trip across Canada next year. 

**FamilyVacay:** We're a family, so we prefer all-inclusive resorts. The kids love swimming pools. We can't do adventure vacations - too stressful!

**CultureSeeker:** I prefer cultural vacations. I'm visiting Quebec City next month - it's a **hidden gem**! Great food and French culture.`
    },
    {
      type: "mc",
      q: "¿Cuántos usuarios utilizan el Presente Continuo para hablar de sus planes?",
      options: [
        "Varios (I'm planning, I'm thinking of, I'm visiting)",
        "Nadie lo usa",
        "Solo una persona"
      ],
      a: "Varios (I'm planning, I'm thinking of, I'm visiting)",
      successtip: "✓ FUTURE PLANS!\nEl uso de -ing es la forma más natural de hablar de planes ya decididos. 📅"
    },
    {
      type: "mc",
      q: "¿Qué significa la expresión 'Hit the road'?",
      options: [
        "Empezar un viaje por carretera",
        "Golpear el pavimento",
        "Construir una calle nueva"
      ],
      a: "Empezar un viaje por carretera",
      successtip: "✓ IDIOM!\n'Hit the road' es la forma clásica de decir que comienza la aventura. 🚗💨"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Decision & Conclusion (preguntas 1, 9 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Final Choice**

**TravelLover99:** Thanks everyone! I think I'm more of a nature person, so I'm thinking of Banff in the fall.

**MountainGirl:** Fall! The leaves are changing color and it's less crowded. Trust me!

**TravelLover99:** Perfect! I'm planning my trip now. I caught the **travel bug** from you all! 😊`
    },

    

    {
      type: "mc",
      q: "¿Cuántas preferencias de vacaciones diferentes se mencionan en total?",
      options: [
        "Muchas (montañas, playa, solo, familia, cultura, naturaleza, presupuesto)",
        "Solo una",
        "Dos"
      ],
      a: "Muchas (montañas, playa, solo, familia, cultura, naturaleza, presupuesto)",
      successtip: "✓ DIVERSITY!\nHas identificado todos los estilos de viaje mencionados. 🌍"
    },
    {
      type: "mc",
      q: "¿Por qué MountainGirl recomienda viajar en otoño (fall)?",
      options: [
        "Hojas de colores, menos gente y es más barato",
        "Solo porque es barato",
        "Ella no lo recomienda"
      ],
      a: "Hojas de colores, menos gente y es más barato",
      successtip: "✓ PERFECT TIMING!\nEl otoño es considerado por muchos la mejor época para visitar las montañas. 🍁"
    },
    {
      type: "mc",
      q: "En conclusión, el mensaje principal de la conversación es:",
      options: [
        "Cada persona tiene gustos diferentes y todos son válidos",
        "Solo hay un tipo de vacación que es buena",
        "No es buena idea viajar solo"
      ],
      a: "Cada persona tiene gustos diferentes y todos son válidos",
      successtip: "✓ READING COMPLETE! 🎉\n¡Felicidades! Has comprendido una conversación grupal compleja con múltiples opiniones y modismos. 🏆"
    }
  ]
},

  // SET 12: Unit Review
  reviewSet: {
    id: "m4-u14-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match vocabulary type:",
        pairs: [
          { left: "Idiom", right: "hit the road, catch some rays" },
          { left: "Slang", right: "road trip, cottage, staycation" },
          { left: "Formal", right: "I would like to visit, I prefer" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "State verbs for preferences (present simple):",
        options: [
          "I love, I hate, I prefer, I want",
          "I'm loving, I'm hating",
          "I loved, I hated"
        ],
        a: "I love, I hate, I prefer, I want"
      },
      {
        type: "mc",
        q: "Present continuous for plans:",
        options: [
          "I'm planning, I'm visiting, I'm thinking of",
          "I plan, I visit",
          "I planned"
        ],
        a: "I'm planning, I'm visiting, I'm thinking of"
      },
      {
        type: "input",
        q: "Complete idiom: 'Let's ___ the road!' (hit = start traveling)",
        a: "hit"
      },
      {
        type: "input",
        q: "Complete slang: 'I'm having a ___ - vacation at home.' (staycation)",
        a: "staycation"
      },
      {
        type: "order",
        q: "Ordena: 'Prefiero la playa a las montañas'",
        words: ["I", "prefer", "the", "beach", "to", "the", "mountains."],
        a: "I prefer the beach to the mountains."
      },
      {
        type: "mc",
        q: "'I can't afford' means:",
        options: [
          "Too expensive for me / no puedo costear",
          "I can buy it easily",
          "I don't want it"
        ],
        a: "Too expensive for me / no puedo costear"
      },
      {
        type: "mc",
        q: "'Hidden gem' vs 'tourist trap':",
        options: [
          "Hidden gem = good find, Tourist trap = overpriced place",
          "Both bad",
          "Both good"
        ],
        a: "Hidden gem = good find, Tourist trap = overpriced place"
      },
      {
        type: "mc",
        q: "'Off-season' = ",
        options: ["Low tourist season (cheaper, less crowded)", "Best time", "Never good time"],
        a: "Low tourist season (cheaper, less crowded)"
      },
      {
        type: "mc",
        q: "FINAL MASTERY - Express yourself:\n\n'What's your dream vacation?'\n\nGood answer uses:",
        options: [
          "YOUR opinion! 'I love/prefer...', 'I'm planning/thinking of...', 'I want to...', explain WHY!",
          "Just 'yes' or 'no'",
          "Copy someone else"
        ],
        a: "YOUR opinion! 'I love/prefer...', 'I'm planning/thinking of...', 'I want to...', explain WHY!",
        successtip: "✓ UNIT 14 COMPLETE! Vacation vocabulary + idioms + slang mastered! 🎉 You can now: express preferences (I love/hate/prefer), make plans (I'm planning/thinking of), use CAN for possibilities, discuss money (can't afford), use idioms (hit the road, itchy feet, travel bug), use slang (staycation, cottage, road trip), ask for recommendations, share opinions, talk about vacation types! From basic vocabulary to natural conversation with idioms! You're expressing yourself like a native speaker! 🌍✈️🗣️"
      }
    ]
  }
};
